import { Component, inject, signal, ViewChild } from '@angular/core';
import { OperationShiftService } from '../../services/operation-shift.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DepartmentService } from 'src/app/modules/shared/services/department.service';
import { Department, Machine } from 'src/app/modules/shared/interface/department.entity';
import { LoadingService } from 'src/app/core/services/loading.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ShiftService } from 'src/app/modules/shared/services/shift.service';
import { DepartmentDTO, MachineDTO, OperationsDTO, ShiftOeprations, ShiftOperationDTO } from '../../interfaces/operation-shift.entity';
import { TimeTrackService } from 'src/app/modules/shared/services/time-track.service';
import { HoldReason } from 'src/app/modules/shared/interface/time-track.entity';
import { ShiftGetByDepartmentDTO } from 'src/app/modules/shared/interface/shift.entity';
import { NgApexchartsModule } from 'ng-apexcharts';
import { GlobalDepartmentService } from 'src/app/core/services/global-department.service';
import {
    ApexChart,
    ApexDataLabels,
    ApexGrid,
    ApexLegend,
    ApexStroke,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
    ChartComponent
} from 'ng-apexcharts';
import { catchError, of, Subscription, tap } from 'rxjs';

export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    dataLabels: ApexDataLabels;
    grid: ApexGrid;
    stroke: ApexStroke;
    tooltip: ApexTooltip;
    legend: ApexLegend;
};

@Component({
    selector: 'app-operation-shift',
    standalone: true,
    imports: [FormsModule, CommonModule, NgApexchartsModule],
    templateUrl: './operation-shift.component.html',
    styleUrl: './operation-shift.component.scss'
})
export class OperationShiftComponent {
    operationShiftService = inject(OperationShiftService);
    departmentService = inject(DepartmentService);
    loading = inject(LoadingService);
    notification = inject(NotificationService);
    shiftService = inject(ShiftService);
    globalDepartmentService = inject(GlobalDepartmentService);
    timeTrackService = inject(TimeTrackService);

    @ViewChild('chart') chart?: ChartComponent;
    @ViewChild('weeklyChart') weeklyChart?: ChartComponent;
    @ViewChild('nosChart') nosChart?: ChartComponent;

    protected $$departmentDTO = signal<Department[]>([]);
    protected $$availableMachines = signal<Machine[]>([]);
    protected $$DepartmentOutput = signal<DepartmentDTO[]>([]);
    protected $$MachineDTO = signal<MachineDTO[]>([]);
    protected $$ShiftGetByDepartmentDTO = signal<ShiftGetByDepartmentDTO[]>([]);
    protected $$ShiftOperationsOutput = signal<ShiftOeprations | null>(null);
    protected $$ShiftOperationDTO = signal<ShiftOperationDTO[]>([]);
    protected $$ShiftsByMachine = signal<Map<number, ShiftGetByDepartmentDTO[]>>(new Map());

    showOperationsPopup = signal(false);
    selectedOperations = signal<OperationsDTO[]>([]);
    selectedShiftInfo = signal<{ machineName: string; shiftName: string; date: string }>({
        machineName: '',
        shiftName: '',
        date: ''
    });

    expandedOperationId = signal<number | null>(null);
    holdReasons = signal<{ [operationId: number]: HoldReason[] }>({});
    loadingHoldReasons = signal<{ [operationId: number]: boolean }>({});

    currentTime: string = '';
    today: string = '';
    private clockSub?: Subscription;
    startDayIndex = signal(0);
    calendarDays: Date[] = [];
    department_id = this.globalDepartmentService.selectedDepartmentId;
    Year = signal(new Date().getFullYear());
    Month = signal(new Date().getMonth());
    selectedYear: number = this.Year();
    selectedMonth: number = this.Month();
    months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    years: number[] = [];
    operatorNames = signal<string[]>([]);

    protected searchParams = signal({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        department: this.globalDepartmentService.getDepartmentId() ?? -1,
        day: 11,
        machine: 56,
        shift: 1
    });

    chartOptions: ChartOptions = {
        series: [{
            name: 'Total Output',
            data: []
        }],
        chart: {
            type: 'bar',
            height: 350,
            toolbar: { show: true }
        },
        xaxis: {
            categories: [],
            title: { text: 'Machines' },
            labels: { rotate: -45, rotateAlways: true }
        },
        yaxis: {
            title: { text: 'Total Output' },
            labels: {
                formatter: (value) => {
                    return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                }
            }
        },
    dataLabels: { 
        enabled: true,
        formatter: (value: any) => {
            return Math.round(Number(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        },
        style: {
            fontSize: '10px',
            fontWeight: 'bold',
            colors: ['#fff']
        },
        background: {
            enabled: true,
            foreColor: '#000',
            borderRadius: 4,
            padding: 6,
            opacity: 0.8,
            borderWidth: 1,
            borderColor: '#ddd'
        },
        offsetY: -5
    },

        grid: { borderColor: '#e7e7e7', row: { colors: ['#f3f3f3', 'transparent'], opacity: 0.5 } },
        stroke: { show: true, width: 2, colors: ['transparent'] },
        tooltip: { 
        y: { 
            formatter: (value: number) => {
                return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }
        } 
    },
        legend: { show: true, position: 'top' }
    };

    weeklyChartOptions: ChartOptions = {
        series: [],
        chart: {
            type: 'bar',
            height: 470,
            toolbar: { show: true }
        },
        xaxis: {
            categories: [],
            title: { text: 'Machines' },
            labels: { rotate: -45, rotateAlways: true }
        },
        yaxis: {
            title: { text: 'Total Output' },
            labels: {
            formatter: (value) => {
                return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }
            }
        },
        dataLabels: { enabled: false },
        grid: { borderColor: '#e7e7e7', row: { colors: ['#f3f3f3', 'transparent'], opacity: 0.5 } },
        stroke: { show: true, width: 2, colors: ['transparent'] },
        tooltip: { y: { formatter: (value) => `${value}` } },
        legend: { show: true, position: 'top' }
    };

    nosChartOptions: ChartOptions = {
        series: [{
            name: 'Total Output',
            data: []
        }],
        chart: {
            type: 'bar',
            height: 350,
            toolbar: { show: true }
        },
        xaxis: {
            categories: [],
            title: { text: 'Machines' },
            labels: { rotate: -45, rotateAlways: true }
        },
        yaxis: {
            title: { text: 'Total Quantity' },
            labels: {
                formatter: (value) => Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
        },
       dataLabels: { 
        enabled: true,
        formatter: (value: any) => {
            return Math.round(Number(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        },
        style: {
            fontSize: '10px',
            fontWeight: 'bold',
            colors: ['#fff']
        },
        background: {
            enabled: true,
            foreColor: '#000',
            borderRadius: 4,
            padding: 6,
            opacity: 0.8,
            borderWidth: 1,
            borderColor: '#ddd'
        },
        offsetY: -5
    },
        grid: { borderColor: '#e7e7e7', row: { colors: ['#f3f3f3', 'transparent'], opacity: 0.5 } },
        stroke: { show: true, width: 2, colors: ['transparent'] },
        tooltip: { y: { formatter: (value) => value.toString() } },
        legend: { show: true, position: 'top' }
    };


    constructor() {
        this.initializeCurrentWeek();
        this.fetchDepartment();
        this.populateYears();
        // this.getOutPut();
       // this.getShiftOperations();
    }

    ngOnInit(): void {
        setInterval(() => this.fetchDepartment(), 5 * 60 * 1000);
        //setInterval(() => this.getShiftOperations(), 5 * 60 * 1000);
    }

    getOutPut() {
        this.loading.set(true);
        this.operationShiftService.getShiftOutputByDep(this.searchParams()).pipe(
            tap(response => {
                this.loading.set(false);
                this.$$DepartmentOutput.set([response.data]);
                this.updateWeeklyChart();
                this.updateAllUnitCharts();
                this.$$MachineDTO.set(response.data.machines)
                console.log('hi', this.$$MachineDTO());
            }),
            catchError(error => {
                this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to fetch shift Output data` });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }

   updateWeeklyChart() {
        const departmentOutput = this.$$DepartmentOutput()[0];
        if (!departmentOutput?.machines || !departmentOutput.machines.length) {
            console.warn('No machines or department data available for weekly chart');
            this.notification.set({ type: 'error', message: 'No data available for the selected period' });
            this.weeklyChartOptions.series = [];
            this.weeklyChartOptions.xaxis = { ...this.weeklyChartOptions.xaxis, categories: [] };
            if (this.weeklyChart) {
                this.weeklyChart.updateOptions(this.weeklyChartOptions);
            }
            return;
        }

        const machineNames = departmentOutput.machines.map(machine => machine.machineName);
        
        const firstDayOfMonth = new Date(this.selectedYear, this.selectedMonth, 1);
        const lastDayOfMonth = new Date(this.selectedYear, this.selectedMonth + 1, 0);
        const weekNumbers = new Set<number>();
        
        let currentDate = firstDayOfMonth;
        while (currentDate <= lastDayOfMonth) {
            const weekNumber = this.getWeekNumber(currentDate);
            weekNumbers.add(weekNumber);
            currentDate = new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000);
        }

        const validWeekNumbers = Array.from(
            new Set(
                departmentOutput.machines
                    .flatMap(machine => machine.weeklyOutput.map(week => week.weekNumber))
                    .filter(week => weekNumbers.has(week))
            )
        ).sort((a, b) => a - b);

        const series = validWeekNumbers.map(weekNumber => ({
            name: `Week ${weekNumber}`,
            data: departmentOutput.machines.map(machine => {
                const weekData = machine.weeklyOutput.find(week => week.weekNumber === weekNumber);
                return weekData?.totalSheets ?? 0;
            })
        }));

        // console.log('Weekly Chart Data:', { machineNames, validWeekNumbers, series });

        this.weeklyChartOptions.series = series;
        this.weeklyChartOptions.xaxis = { ...this.weeklyChartOptions.xaxis, categories: machineNames };
        if (this.weeklyChart) {
            this.weeklyChart.updateOptions(this.weeklyChartOptions);
        }
    }

    private getWeekNumber(date: Date): number {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    }

   onDepartmentChange(departmentId: number): void {
    this.globalDepartmentService.setDepartmentId(departmentId);
    this.searchParams.update(params => ({ ...params, department: departmentId }));
 
    const selectedDept = this.$$departmentDTO().find(d => d.departmentId === Number(departmentId));

    if (selectedDept) {
        this.$$availableMachines.set(selectedDept.machines);        
        this.$$ShiftsByMachine.set(new Map());                      
        this.loadShiftsForMachines(selectedDept.machines);            
    }

    this.getOutPut();
    this.getShiftsByDepartment(Number(departmentId));
    }

    populateYears(): void {
        const startYear = 2025;
        this.years = Array.from({ length: 11 }, (_, i) => startYear + i);
    }

     public fetchDepartment(): void {
  this.departmentService.getAllDepartments().subscribe({
    next: (result) => {
        if (result.data && result.data.departments) {
          const departments = result.data.departments;
          this.$$departmentDTO.set(departments);
          
          let currentDeptId = this.globalDepartmentService.getDepartmentId();
          if (!currentDeptId && departments.length > 0) {
            currentDeptId = departments[0].departmentId;
            this.globalDepartmentService.setDepartmentId(currentDeptId);
          }

          if (currentDeptId) {
            const selectedDept = departments.find(d => d.departmentId === currentDeptId);
            if (selectedDept) {
              this.$$availableMachines.set(selectedDept.machines);
              this.loadShiftsForMachines(selectedDept.machines);
            }
            
            this.searchParams.set({ ...this.searchParams(), department: currentDeptId });
            this.getOutPut();
            this.getShiftsByDepartment(currentDeptId);
          } else {
            this.notification.set({ type: 'error', message: 'No departments available' });
          }
        }
    },
    error: (error) => {
      console.error('Error fetching departments:', error);
      this.notification.set({ type: 'error', message: 'Failed to fetch departments' });
    }
  });
}

    generateCalendar(): void {
        const year = this.selectedYear;
        const month = this.selectedMonth;
        const daysInMonthMap = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

        if (month === 1) {
            const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
            daysInMonthMap[1] = isLeapYear ? 29 : 28;
        }

        const daysInMonth = daysInMonthMap[month];

        if (this.startDayIndex() + 7 > daysInMonth) {
            this.startDayIndex.set(Math.max(0, daysInMonth - 7));
        }

        this.calendarDays = Array.from(
            { length: 7 },
            (_, i) => new Date(year, month, this.startDayIndex() + i + 1)
        );
    }

    initializeCurrentWeek(): void {
        const today = new Date();
        this.selectedYear = today.getFullYear();
        this.selectedMonth = today.getMonth();

        const currentDate = today.getDate();
        const startIndex = Math.floor((currentDate - 1) / 7) * 7;
        this.startDayIndex.set(startIndex);

        this.generateCalendar();
    }

    updateCalendarYear(year: number): void {
        this.searchParams.update(params => ({
            ...params,
            year: year
        }));
        this.generateCalendar();
        this.getOutPut();
        //this.getShiftOperations();
    }

    updateCalendarMonth(month: number): void {
        const newMonth = +month + 1;
        this.searchParams.update(params => ({
            ...params,
            month: newMonth
        }));
        this.generateCalendar();
        this.getOutPut();
        //this.getShiftOperations();
    }

    previousWeek(): void {
        if (this.startDayIndex() > 0) {
            this.startDayIndex.set(this.startDayIndex() - 7);
            this.generateCalendar();
        }
    }

    nextWeek(): void {
        const daysInMonth = new Date(this.selectedYear, this.selectedMonth + 1, 0).getDate();
        if (this.startDayIndex() + 7 < daysInMonth) {
            this.startDayIndex.set(this.startDayIndex() + 7);
            this.generateCalendar();
        }
    }

    get daysInCurrentMonth(): number {
        return new Date(this.selectedYear, this.selectedMonth + 1, 0).getDate();
    }

    getShiftsByDepartment(id: number) {
        this.shiftService.getById(String(id)).subscribe({
            next: (result) => {
                if (result.data.shifts) {
                    this.$$ShiftGetByDepartmentDTO.set(result.data.shifts);
                }
            },
            error: (error) => {
                console.error('Error fetching Operators:', error);
                this.notification.set({ type: 'error', message: `Failed to Fetch Shifts.` });
            }
        });
    }

    getShiftData(machine: MachineDTO, day: Date) {
        const dateStr = day.toLocaleDateString('en-CA'); 
        return machine.dailyShiftCounts.find(d => d.date === dateStr) || null;
    }

    getShiftOutput(shiftData: any, shiftId: number): string {
        const shift = shiftData?.shifts?.find((s: any) => s.shiftId === shiftId);
        
        if (!shift) {
            return '-';
        }

        const value = shift.noOfSheets;
        const makeReady = shift.makeReadyCount;
        const changeovers = shift.changeoverCount;

        if ((!value || Number(value) === 0 || isNaN(Number(value))) && 
            (!makeReady || Number(makeReady) === 0) && 
            (!changeovers || Number(changeovers) === 0)) {
            return '-';
        }

        return Number(value || 0).toLocaleString('en-US');
    }

    getMakeReadyCount(shiftData: any, shiftId: number): string {
        const shift = shiftData?.shifts?.find((s: any) => s.shiftId === shiftId);
        const makeReady = shift?.makeReadyCount;
        
        return Number(makeReady || 0).toLocaleString('en-US');

    }

    getMakeReadyCountNum(shiftData: any, shiftId: number): number {
        const shift = shiftData?.shifts?.find((s: any) => s.shiftId === shiftId);
        return shift?.makeReadyCount ?? 0;
    }

    getShiftChangeovers(shiftData: any, shiftId: number): number {
        const shift = shiftData?.shifts?.find((s: any) => s.shiftId === shiftId);
        return shift?.changeoverCount ?? 0;
    }

    formatMakeReadyHours(hours: number | null | undefined): string {
        if (!hours) return '0h 0m';
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        return `${h}h ${m}m`;
    }
    
   getShiftOperations(machine: MachineDTO, dateStr: string, shiftId: number) {

    this.loading.set(true);

    this.operationShiftService.getShiftOperationsByDep(this.searchParams()).pipe(
        tap(response => {

            this.loading.set(false);

            const shiftOperationsData = response.data;
            this.$$ShiftOperationsOutput.set(shiftOperationsData);

            const machineOperations = shiftOperationsData.machines.find(
                m => m.machineId === machine.machineId
            );

            const dailyOperation = machineOperations?.dailyShiftOperations.find(
                d => d.date === dateStr
            );

            const shiftOperation = dailyOperation?.shifts.find(
                s => s.shiftId === shiftId
            );

            const jobs = (shiftOperation?.jobs ?? []).filter(j => j.totalSheets > 0 || j.makeReadyCount > 0);

            // Sort jobs by startedDateTime
            jobs.sort((a, b) => {
                const dateA = a.startedDateTime ? new Date(a.startedDateTime).getTime() : 0;
                const dateB = b.startedDateTime ? new Date(b.startedDateTime).getTime() : 0;
                return dateA - dateB;
            });

            // Collect all unique operator names from all jobs in this shift
            const allOperatorNames = [
                ...new Set(
                    jobs.flatMap(j => j.operators.map(op => op.operatorName))
                )
            ];
            this.operatorNames.set(allOperatorNames);

            this.selectedOperations.set(jobs);
            this.showOperationsPopup.set(true);

        }),
        catchError(error => {
            this.loading.set(false);
            this.notification.set({ type: 'alert', message: `No operation data found` });
            console.error(error);
            return of(null);
        })
    ).subscribe();
}

    onShiftCellClick(machine: MachineDTO, day: Date, shiftId: number): void {

    const dateStr = day.toLocaleDateString('en-CA');
    const date = new Date(dateStr);
    const shift = this.$$ShiftGetByDepartmentDTO().find(s => s.shiftId === shiftId);

    this.selectedShiftInfo.set({
        machineName: machine.machineName,
        shiftName: shift?.shiftName || `Shift ${shiftId}`,
        date: date.toLocaleDateString('en-US', { 
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    });

    this.searchParams.update(params => ({
        ...params,
        shift: shiftId,
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        machine: machine.machineId
    }));

    this.getShiftOperations(machine, dateStr, shiftId);
}

    closeOperationsPopup(): void {
        this.showOperationsPopup.set(false);
        this.selectedOperations.set([]);
        this.expandedOperationId.set(null);
    }

    toggleJobDetails(operation: OperationsDTO): void {
        const opId = operation.operationId;
        
        if (this.expandedOperationId() === opId) {
            this.expandedOperationId.set(null);
            return;
        }
        
        this.expandedOperationId.set(opId);

        if (!this.holdReasons()[opId]) {
            this.loadingHoldReasons.update(state => ({ ...state, [opId]: true }));
            
            const params = {
                ...this.searchParams(),
                operation_id: opId
            };
            
            this.timeTrackService.getHoldReasonsByOperation(params).subscribe({
                next: (res) => {
                    this.holdReasons.update(state => ({ ...state, [opId]: res.data }));
                    this.loadingHoldReasons.update(state => ({ ...state, [opId]: false }));
                },
                error: (err) => {
                    console.error('Failed to fetch hold reasons:', err);
                    this.loadingHoldReasons.update(state => ({ ...state, [opId]: false }));
                }
            });
        }
    }

    // getShiftOperationsCount(machine: MachineDTO, day: Date, shiftId: number): number {
    //     const dateStr = day.toLocaleDateString('en-CA');
    //     const shiftOperationsData = this.$$ShiftOperationsOutput()[0];
        
    //     if (!shiftOperationsData) {
    //         return 0;
    //     }

    //     const machineOperations = shiftOperationsData.machines.find(
    //         m => m.machineName === machine.machineName || m.machineId === machine.machineId
    //     );

    //     if (!machineOperations) {
    //         return 0;
    //     }

    //     const dailyOperation = machineOperations.dailyShiftOperations.find(
    //         d => d.date === dateStr
    //     );

    //     if (!dailyOperation) {
    //         return 0;
    //     }

    //     const shiftOperation = dailyOperation.shifts.find(
    //         s => s.shiftId === shiftId
    //     );

    //     if (!shiftOperation?.jobs) return 0;

    //     const filteredJobs = shiftOperation.jobs.filter(j =>
    //         !(j.totalSheets === 0 && j.operations.every(op => op.jobStatus?.toLowerCase() === 'completed'))
    //     );

    //     return filteredJobs.length;
    // }

    getSelectedDepartmentName(): string {
        const dept = this.$$departmentDTO()
            ?.find(d => d.departmentId == this.department_id());

        return dept ? dept.departmentName : 'Select Department';
    }

    loadShiftsForMachines(machines: Machine[]): void {
  const shiftsMap = new Map<number, ShiftGetByDepartmentDTO[]>();
  let completed = 0;

  machines.forEach(machine => {
    this.shiftService.getById(String(machine.machineId)).subscribe({
      next: (result) => {
        if (result.data.shifts) {
          shiftsMap.set(machine.machineId, result.data.shifts);
        }
        completed++;
        if (completed === machines.length) {
          this.$$ShiftsByMachine.set(new Map(shiftsMap));
        }
      },
      error: () => {
        completed++;
        if (completed === machines.length) {
          this.$$ShiftsByMachine.set(new Map(shiftsMap));
        }
      }
    });
  });
}

// Add this signal to track dynamic chart configs
protected $$unitCharts = signal<{ unit: string; chartOptions: ChartOptions }[]>([]);

buildChartOptionsForUnit(unit: string, machines: MachineDTO[]): ChartOptions {
    const filtered = machines.filter(m => m.unitOfMachine?.toLowerCase() === unit.toLowerCase());
    const machineNames = filtered.map(m => m.machineName);
    const totalSheets = filtered.map(m => m.monthlyOutput?.totalSheets ?? 0);

    return {
        series: [{ name: `Total Output (${unit.toUpperCase()})`, data: totalSheets }],
        chart: { type: 'bar', height: 350, toolbar: { show: true } },
        xaxis: {
            categories: machineNames,
            title: { text: 'Machines' },
            labels: { rotate: -45, rotateAlways: true }
        },
        yaxis: {
            title: { text: `Total ${unit.toUpperCase()}` },
            labels: {
                formatter: (value) => Math.round(value).toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
        },
        dataLabels: {
            enabled: true,
            formatter: (value: any) => Math.round(Number(value)).toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ","),
            style: { fontSize: '10px', fontWeight: 'bold', colors: ['#fff'] },
            background: {
                enabled: true, foreColor: '#000', borderRadius: 4,
                padding: 6, opacity: 0.8, borderWidth: 1, borderColor: '#ddd'
            },
            offsetY: -5
        },
        grid: { borderColor: '#e7e7e7', row: { colors: ['#f3f3f3', 'transparent'], opacity: 0.5 } },
        stroke: { show: true, width: 2, colors: ['transparent'] },
        tooltip: {
            y: { formatter: (value: number) => value.toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",") }
        },
        legend: { show: true, position: 'top' }
    };
}

updateAllUnitCharts(): void {
    const departmentOutput = this.$$DepartmentOutput()[0];
    if (!departmentOutput?.machines?.length) {
        this.$$unitCharts.set([]);
        return;
    }

    // Get all unique units dynamically
    const uniqueUnits = [
        ...new Set(
            departmentOutput.machines
                .map(m => m.unitOfMachine?.toLowerCase())
                .filter(Boolean)
        )
    ] as string[];

    const charts = uniqueUnits.map(unit => ({
        unit: unit.toUpperCase(),
        chartOptions: this.buildChartOptionsForUnit(unit, departmentOutput.machines)
    }));

    this.$$unitCharts.set(charts);
}

}